import { formatBusinessDateIso, getBusinessDateParts, todayBusinessMs } from '@/utils/date-utils';

/** `associations.dues_cadence` — the literal strings, as the Worker and the DB hold them. */
export type DuesCadence = 'monthly' | 'quarterly' | 'annual';

/**
 * Dues period keys by cadence: monthly `2026-10`, quarterly `2026-Q4`, annual `2026`. The key
 * is the idempotency half of every dues charge's `source_ref` (`<account id>:<period>`), so an
 * association must only ever be posted under ITS cadence's keys — an annual association posted
 * as `2027-01` and later as `2027` is two periods, and two bills.
 *
 * Shared by the Worker (which normalizes whatever period a caller sends) and the Post Dues page
 * (which picks the default and snaps the picker), so the two cannot disagree.
 */

const MONTH_KEY = /^(\d{4})-(\d{2})$/;
const QUARTER_KEY = /^(\d{4})-Q([1-4])$/;
const YEAR_KEY = /^(\d{4})$/;

/** Months between period starts. */
const STEP: Record<DuesCadence, number> = { monthly: 1, quarterly: 3, annual: 12 };

/** The first month (1–12) of the period that `month` (1–12) falls in. */
const periodStartMonth = (cadence: DuesCadence, month: number): number =>
	month - ((month - 1) % STEP[cadence]);

/** `YYYY-MM` for a year + 1-based month. */
const monthKey = (year: number, month: number): string => `${year}-${String(month).padStart(2, '0')}`;

/** A `YYYY-MM`, `YYYY-QN` or `YYYY` key as the `{ year, month }` its period starts on. */
const parsePeriodStart = (period: string): { year: number; month: number } | null => {
	const m = period.match(MONTH_KEY);
	if (m) {
		const month = Number(m[2]);
		return month >= 1 && month <= 12 ? { year: Number(m[1]), month } : null;
	}
	const q = period.match(QUARTER_KEY);
	if (q) return { year: Number(q[1]), month: (Number(q[2]) - 1) * 3 + 1 };
	const y = period.match(YEAR_KEY);
	if (y) return { year: Number(y[1]), month: 1 };
	return null;
};

/**
 * The cadence's period key for a requested period, or null when the request does not START one
 * of the cadence's periods — `2026-10` is no annual period, so a portfolio-wide October run
 * skips annual associations instead of billing them under a key of the wrong shape.
 */
export const cadencePeriod = (cadence: DuesCadence, period: string): string | null => {
	const start = parsePeriodStart(period);
	if (!start || periodStartMonth(cadence, start.month) !== start.month) return null;
	if (cadence === 'monthly') return monthKey(start.year, start.month);
	if (cadence === 'quarterly') return `${start.year}-Q${Math.floor((start.month - 1) / 3) + 1}`;
	return String(start.year);
};

/** `YYYY-MM` of the first month of the period containing `month` (`YYYY-MM`). */
export const snapToPeriodStart = (cadence: DuesCadence, month: string): string => {
	const start = parsePeriodStart(month);
	return start ? monthKey(start.year, periodStartMonth(cadence, start.month)) : month;
};

/**
 * The Post Dues default: the next dues date ON OR AFTER `todayMs` (an ET business day), as a
 * midnight-UTC business-date marker. A dues date is a period's first month on `duesDay`, clamped to
 * the month's length (day 31 bills Feb 28). Dues go out in advance, for every cadence: on
 * 2026-10-28 an annual association defaults to 2027-01-01, a monthly one billed on the 1st to
 * 2026-11-01. Today itself counts when it IS a dues date. Its `YYYY-MM` is the period to post.
 */
export const nextDuesPeriodStart = (
	cadence: DuesCadence,
	duesDay: number | null | undefined,
	todayMs: number = todayBusinessMs(),
): number => {
	const { year, month } = getBusinessDateParts(todayMs);
	const day = Math.max(1, duesDay ?? 1);
	for (let start = periodStartMonth(cadence, month); ; start += STEP[cadence]) {
		const y = year + Math.floor((start - 1) / 12);
		const m = ((start - 1) % 12) + 1;
		const lastDay = new Date(Date.UTC(y, m, 0)).getUTCDate();
		const candidate = Date.UTC(y, m - 1, Math.min(day, lastDay));
		if (candidate >= todayMs) return candidate;
	}
};

/** A period as the PM reads it: `2027 (annual)`, `2027-Q1 (quarterly)`, `2026-11 (monthly)`. */
export const duesPeriodLabel = (cadence: DuesCadence, month: string): string =>
	`${cadencePeriod(cadence, month) ?? month} (${cadence})`;

/** One association as a portfolio-wide Post Dues run sees it (`GET /accounting/post-dues`). */
export interface DuesScheduleRow {
	associationId: string;
	name: string;
	cadence: DuesCadence;
	dayOfMonth: number | null;
	/** `dues_start_on`, a business-date marker. */
	startOn: number | null;
}

/**
 * The portfolio Post Dues default: the EARLIEST next dues date across the run's associations, each
 * on its own cadence and day. A portfolio run carries ONE period and the server skips every
 * association that period starts nothing for — defaulting to next month's 1st on Jan 10 skipped an
 * annual association due Jan 15. The server skips associations with no dues day, so they do not
 * count; one that has not started yet counts from its start. Null when none qualifies.
 */
export const nextPortfolioDuesDate = (rows: DuesScheduleRow[], todayMs: number = todayBusinessMs()): number | null => {
	const dates = rows
		.filter((r) => r.dayOfMonth != null)
		.map((r) => nextDuesPeriodStart(r.cadence, r.dayOfMonth, Math.max(todayMs, r.startOn ?? 0)));
	return dates.length > 0 ? Math.min(...dates) : null;
};

/**
 * Whether a portfolio run for `forPeriod` (`YYYY-MM`) posted on `postingDateMs` bills this
 * association, and if not, why. Mirrors the server's portfolio skips in postRecurringAssessments:
 * no dues day, dues not started by the post date, and a month that starts none of its periods.
 */
export const portfolioDuesStatus = (
	row: DuesScheduleRow,
	postingDateMs: number,
	forPeriod: string,
): { posts: true; period: string } | { posts: false; reason: string } => {
	if (row.dayOfMonth == null) return { posts: false, reason: 'No dues day set — post it from the association' };
	if (row.startOn != null && row.startOn > postingDateMs) {
		return { posts: false, reason: `Dues start ${formatBusinessDateIso(row.startOn)}` };
	}
	const period = cadencePeriod(row.cadence, forPeriod);
	if (period !== null) return { posts: true, period };
	const next = formatBusinessDateIso(nextDuesPeriodStart(row.cadence, row.dayOfMonth, postingDateMs));
	return {
		posts: false,
		reason: `${forPeriod} starts no ${row.cadence} period — next is ${duesPeriodLabel(row.cadence, next.slice(0, 7))} on ${next}`,
	};
};

/**
 * The MONTH keys an annual or quarterly period may have been stored under before 2.7.0 — the Post
 * Dues page sent a month and the Worker used it verbatim. As the half-open range `[from, to)` of
 * `YYYY-MM` keys the period spans: `2027` → `2027-01`..`2027-13`, `2026-Q3` → `2026-07`..`2026-10`.
 * Null for a monthly key, which never had another shape. `to` may be month 13: it only bounds a
 * string range, and `2027-13` sorts after every `2027-12…` ref and before `2027-Q…`.
 */
export const legacyMonthRange = (period: string): { from: string; to: string } | null => {
	const start = parsePeriodStart(period);
	if (!start || MONTH_KEY.test(period)) return null;
	const months = QUARTER_KEY.test(period) ? 3 : 12;
	const pad = (m: number): string => `${start.year}-${String(m).padStart(2, '0')}`;
	return { from: pad(start.month), to: pad(start.month + months) };
};

/** The memo on a dues invoice and its charges, named for the period's cadence. */
export const duesMemo = (period: string): string => {
	const label = QUARTER_KEY.test(period) ? 'Quarterly' : YEAR_KEY.test(period) ? 'Annual' : 'Monthly';
	return `${label} Association Fee · ${period}`;
};

/** Small words an ALL-CAPS chart name spells out, lower-cased mid-name ("Lawn and Snow"). */
const MINOR_WORDS = new Set(['and', 'of', 'the', 'for', 'to', 'in', 'on']);

/**
 * A GL account name as printed on a bill. Chart names are often ALL CAPS ("CAPITAL RESERVES");
 * those read as Title Case, except short acronyms (HOA, R&M — three letters or fewer), which stay
 * upper case. A mixed-case name is printed as entered.
 */
export const printedGlName = (name: string): string => {
	if (name !== name.toUpperCase()) return name;
	return name.split(' ').map((word, i) => {
		const lower = word.toLowerCase();
		if (i > 0 && MINOR_WORDS.has(lower)) return lower;
		if (word.replace(/[^A-Z]/g, '').length <= 3) return word;
		return lower.replace(/\b[a-z]/g, (c) => c.toUpperCase());
	}).join(' ');
};

/**
 * The memo on ONE split dues charge: the period's memo plus what the line pays for — its own
 * label, else its GL account's name. Without the GL fallback a two-line split with unlabelled
 * lines prints the same text twice and the owner cannot tell Capital Reserve from Operating.
 */
export const duesLineMemo = (period: string, line: { label?: string | null; glName?: string | null }): string => {
	const glName = line.glName?.trim();
	const what = line.label?.trim() || (glName ? printedGlName(glName) : '');
	return what ? `${duesMemo(period)} · ${what}` : duesMemo(period);
};
